import React from 'react';

import type { NextPage } from 'next';
import { useRouter } from 'next/router';

import { CheckIcon } from '@heroicons/react/outline';
import { NextSeo } from 'next-seo';

import {
  BottomGraphic,
  FadeIn,
  Footer,
  GradientBg,
  Header,
  InsuranceCarousel,
  PageHeader
} from '../components';
import { StartupInsuranceTabs } from '../components/StartupInsuranceTabs';
import { StartupSvg } from '../components/svg/Startup';
import { startup } from '../constants/insuranceTypes';
import MarQuee from 'react-fast-marquee';

const reasons = [
  {
    name: 'Investor Requirements',
    description: 'Venture capital and other investor requirements'
  },
  {
    name: 'Contractual Requirements',
    description: 'Lender or lease requirements'
  },
  {
    name: 'Lawsuit',
    description: 'Reputational damage and customer lawsuit'
  },
  {
    name: 'Accidents',
    description: 'Employees accidents and injuries'
  },
  {
    name: 'Loss Prevention',
    description: 'Lost, stolen, or damaged property and inventory'
  },
  {
    name: 'Defects',
    description: 'Defective or malfunctioning products'
  }
];

const types = Object.keys(startup).map(type => ({
  title: type,
  icon: startup[type]
}));

const companyLogos = [
  { id: 1, image: "/img/company-logos/brex.png" },
  { id: 2, image: "/img/company-logos/ramp.jpg" },
  { id: 3, image: "/img/company-logos/yc.png" },
  { id: 4, image: "/img/company-logos/carta.png" },
  { id: 5, image: "/img/company-logos/wework.png" },
  { id: 6, image: "/img/company-logos/mercury.png" }
]

const Startup: NextPage = () => {
  const router = useRouter();
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === '/' ? '' : router.asPath)
  ).split('?')[0];
  return (
    <>
      <NextSeo
        title="Startup"
        description="Startup insurance for founders: D&O, EPL, cyber, and GL. Meet investor and contract requirements with coverage that scales from seed through growth. — Coverdash"
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: 'Startup',
          description:
            'Startup insurance for founders: D&O, EPL, cyber, and GL. Meet investor and contract requirements with coverage that scales from seed through growth. — Coverdash'
        }}
      />
      <div className="overflow-hidden">
        <GradientBg />
        <Header />

        {/* Hero */}
        <PageHeader
          title="Startup"
          description="Don't let unnecessary business risks make your founder journey bumpier than it needs to be"
          image={
            <StartupSvg className="mx-auto max-w-4xl w-full mt-8 md:-mt-20" />
          }
        />

        {/* Section-1 */}
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:px-8 lg:grid lg:grid-cols-5 lg:gap-x-8">
          <div className="lg:col-span-2 text-center lg:text-left">
            <FadeIn>
              <p className="text-base font-semibold text-primary uppercase tracking-wide">
                Everything you need
              </p>
            </FadeIn>
            <FadeIn>
              <h2 className="mt-2 text-5xl font-extrabold text-alt">
                Why do <span className="text-primary">startups</span> need
                insurance?
              </h2>
            </FadeIn>

            <p className="mt-4 text-lg text-gray-500">
              Startups are often left in the dark when it comes to protecting
              what they have built. No matter what type of startup you operate
              or whatever stage (Pre-Funded to Series D+), each one comes with a
              particular set of common risks. Obtaining the right insurance is
              crucial to protect your startup from:
            </p>
          </div>
          <div className="mt-12 lg:mt-0 lg:col-span-3">
            <dl className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col sm:gap-x-6 sm:gap-y-10 lg:gap-x-8">
              {reasons.map(reason => (
                <div key={reason.name} className="relative">
                  <dt>
                    <CheckIcon
                      className="absolute h-6 w-6 text-green-500"
                      aria-hidden="true"
                    />
                    <p className="ml-9 text-lg leading-6 font-medium text-gray-900">
                      {reason.name}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-9 text-base text-gray-500">
                    {reason.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* Company Logos Marquee */}
        <FadeIn>
          <MarQuee autoFill className='flex items-center'>
            {companyLogos.map((company, index) => (  
              <div key={index} className="h-auto w-20 md:w-28 lg:w-40 relative mx-4 lg:mx-8">
                <img
                  src={company.image}
                  alt="Company Logo"
                  className="h-full w-full filter grayscale"
                />
              </div>
            ))}
          </MarQuee>
        </FadeIn>

        {/* Section-2 */}
        <div className="max-w-7xl mx-auto py-8 lg:py-20 px-4 sm:px-6 lg:pg-8 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto mt-2 text-5xl font-extrabold text-alt">
              What does <span className="text-primary">startup</span> insurance
              cover?{' '}
            </h2>
          </FadeIn>
          <div className="max-w-2xl mx-auto text-xl text-gray-500">
            <p className="mt-6">
              Choosing the right startup insurance can help protect you from
              costly risks and liabilities, the right policies for your business
              will depend what product you sell, what services you offer, and
              where you are located.
            </p>
            <p className="mt-4">
              Here are some of the most popular insurance policies for startups,
              covering the most common risks you'll face as a startup:
            </p>
          </div>
          <div className="mt-12">
            <StartupInsuranceTabs />
          </div>
        </div>

        {/* Section-3 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl px-4 sm:px-6 lg:px-8 py-8 lg:py-20 text-center">
          <FadeIn>
            <h2 className="max-w-5xl mx-auto mt-2 text-5xl font-extrabold text-alt">
              What types of <span className="text-primary">startups</span> do we
              cover?
            </h2>
          </FadeIn>
          <div className="mt-12 px-2 md:px-12 sm:px-0">
            <InsuranceCarousel items={types} />
          </div>
        </div>

        {/* Section-4 */}
        <div className="mx-auto max-w-3xl lg:max-w-7xl py-8 lg:py-20 px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <FadeIn>
                <h2 className="mt-2 text-5xl font-extrabold text-alt text-center sm:text-left">
                  How much does{' '}
                  <span className="text-primary">startup insurance</span> cost?
                </h2>
              </FadeIn>
              <div className="mt-4 text-lg text-gray-500 text-center sm:text-left">
                <p>
                  Coverdash helps you identify the appropriate coverage to suit
                  your startup needs without breaking the bank. The exact amount
                  you pay will depend on:
                </p>

                <ul className="list-disc p-5 text-left">
                  <li>The type of startup you operate</li>
                  <li>Your revenue and employee payroll</li>
                  <li>Your number of employees</li>
                  <li>Your business location</li>
                </ul>
              </div>
            </div>
            <div className="justify-right items-right">
              <img
                src="/img/insurance-cost.jpg"
                className="h-full w-full lg:w-[140%]"
                alt="illustration of woman pointing at infographic"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <BottomGraphic />
        <Footer />
      </div>
    </>
  );
};

export default Startup;
